import React from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { getBaseApiUrl } from './EnvironmentManager';
import {
  FaCar, FaMusic, FaGraduationCap, FaFlask, FaDice, FaShieldHalved,
  FaDragon, FaRocket, FaHeart, FaCity, FaUtensils, FaShapes, FaIndustry,
  FaBuilding, FaPalette, FaFaceSmile, FaCompactDisc, FaTree,
  FaPaw, FaUserGroup, FaSnowflake, FaPersonRunning
} from 'react-icons/fa6';
import { IconType } from 'react-icons';

interface ContentRequestParams {
  userId: string;
  action: string;
  prompt?: string;
  vendor?: string;
}

const getContentApiUrl = () => {
  return `${getBaseApiUrl()}/content`;
};

export interface ImageTag {
  tag: string;
  label: string;
  icon: IconType;
}

export const IMAGE_TAGS: ImageTag[] = [
  { tag: "sports-fitness", label: "Sports & Fitness", icon: FaPersonRunning },
  { tag: "cars-transportation", label: "Cars & Transportation", icon: FaCar },
  { tag: "music-entertainment", label: "Music & Entertainment", icon: FaMusic },
  { tag: "college-education", label: "College & Education", icon: FaGraduationCap },
  { tag: "science-technology", label: "Science & Tech", icon: FaFlask },
  { tag: "gambling-casino", label: "Gambling & Casino", icon: FaDice },
  { tag: "military-patriotic", label: "Military & Patriotic", icon: FaShieldHalved },
  { tag: "fantasy-surreal", label: "Fantasy & Surreal", icon: FaDragon },
  { tag: "space-scifi", label: "Space & Sci-Fi", icon: FaRocket },
  { tag: "health-lifestyle", label: "Health & Lifestyle", icon: FaHeart },
  { tag: "city-urban", label: "City & Urban", icon: FaCity },
  { tag: "food-drink", label: "Food & Drink", icon: FaUtensils },
  { tag: "modern-minimalistic", label: "Modern & Minimal", icon: FaShapes },
  { tag: "industrial", label: "Industrial", icon: FaIndustry },
  { tag: "architecture-design", label: "Architecture & Design", icon: FaBuilding },
  { tag: "artistic", label: "Artistic", icon: FaPalette },
  { tag: "cartoon", label: "Cartoon", icon: FaFaceSmile },
  { tag: "vintage-retro", label: "Vintage & Retro", icon: FaCompactDisc },
  { tag: "nature-outdoors", label: "Nature & Outdoors", icon: FaTree },
  { tag: "animals-insects", label: "Animals & Insects", icon: FaPaw },
  { tag: "people", label: "People", icon: FaUserGroup },
  { tag: "holiday-seasonal", label: "Holiday & Seasonal", icon: FaSnowflake }
];

const useContentApi = () => {
  const { getAccessTokenSilently } = useAuth0();

  /**
   * Generate a name and tags based on a prompt.
   * @param {ContentRequestParams} params - Parameters for generating a name and tags.
   * @returns {Promise<{ imageName: string, imageTags: string[] }>} - Generated name and valid tags.
   */
  const generateImageContent = async (
    params: ContentRequestParams
  ): Promise<{ imageName: string; imageTags: string[] }> => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(getContentApiUrl(), params, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response?.status === 200) {
        const { imageName, imageTags } = response.data?.content || {};

        if (!imageName) {
          throw new Error('Invalid response format: "imageName" is missing.');
        }

        // Validate that tags exist in IMAGE_TAGS
        const validTagSet = new Set(IMAGE_TAGS.map((t) => t.tag));
        const validTags = (Array.isArray(imageTags) ? imageTags : []).filter((tag) =>
          validTagSet.has(tag)
        );

        return { imageName, imageTags: validTags };
      } else {
        throw new Error(`Failed to generate content. Response status: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error generating image content:', error.response?.data || error.message);
      throw new Error('Failed to generate image content.');
    }
  };


  /**
   * Retrieve prompt ideas.
   * @returns {Promise<string[]>} - List of prompt ideas.
   */
  const generatePromptIdeas = async (): Promise<string[]> => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.get(getContentApiUrl(), {
        params: {
          operation: 'generatePromptIdeas',
          userId: 0,
        },
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response?.status === 200) {
        const promptIdeas = response.data?.data?.content?.promptIdeas;
        if (!Array.isArray(promptIdeas)) {
          throw new Error('Invalid response format: "promptIdeas" must be an array.');
        }
        return promptIdeas;
      } else {
        console.error(`Error retrieving prompt ideas (status=${response?.status}):`);
        throw new Error('Failed to retrieve prompt ideas. Response status not successful.');
      }
    } catch (error: any) {
      console.error('Error retrieving prompt ideas:', error.response?.data || error.message);
      throw new Error('Failed to retrieve prompt ideas.');
    }
  };
  return { generateImageContent, generatePromptIdeas };
};

export default useContentApi;
